import { CALL_API } from 'redux-api-middleware';
import {
  union,
  without,
  keyBy,
  mapValues,
  forEach,
  every,
  range,
  pick,
  omit,
  get
} from 'lodash';
import uuidV4 from 'uuid/v4';

import { createSelector } from 'reselect';
import { withConfirmation } from 'modules/confirmation';
import { getConfig } from 'modules/config';

import { guid } from 'helpers/misc';
import { reduceReducers, createReducer } from 'helpers/redux';
import { commonHeaders } from 'helpers/api';
import paginate, { makeSelectors } from 'helpers/redux/paginate';

export const namespace = 'data-schema';
export const validActions = ['view', 'edit', 'publish', 'deactivate', 'delete'];
export const uiStateKey = 'data-schema-table';
export const defaultFilter = 'org-schemas';

export const FETCH = 'vtn/data-schema/FETCH';
export const FETCH_SUCCESS = 'vtn/data-schema/FETCH_SUCCESS';
export const FETCH_FAILURE = 'vtn/data-schema/FETCH_FAILURE';

const CREATE = 'vtn/data-schema/CREATE';
export const CREATE_SUCCESS = 'vtn/data-schema/CREATE_SUCCESS';
const CREATE_FAILURE = 'vtn/data-schema/CREATE_FAILURE';

const UPDATE = 'vtn/data-schema/UPDATE';
const UPDATE_SUCCESS = 'vtn/data-schema/UPDATE_SUCCESS';
export const UPDATE_FAILURE = 'vtn/data-schema/UPDATE_FAILURE';
export const UPDATE_SCHEMA_SUCCESS = 'vtn/data-schema/UPDATE_SCHEMA_SUCCESS';

const DELETE = 'vtn/data-schema/DELETE';
export const DELETE_SUCCESS = 'vtn/data-schema/DELETE_SUCCESS';
export const DELETE_FAILURE = 'vtn/data-schema/DELETE_FAILURE';

const LISTING_DATA_SCHEMAS = 'vtn/data-schema/LISTING_DATA_SCHEMAS';
const DONE_LISTING_DATA_SCHEMAS = 'vtn/data-schema/DONE_LISTING_DATA_SCHEMAS';

const LIST_RELATED_DATA_SCHEMAS = 'vtn/data-schema/LIST_RELATED_DATA_SCHEMAS';
export const LIST_RELATED_DATA_SCHEMAS_SUCCESS =
  'vtn/data-schema/LIST_RELATED_DATA_SCHEMAS_SUCCESS';
export const LIST_RELATED_DATA_SCHEMAS_FAILURE =
  'vtn/data-schema/LIST_RELATED_DATA_SCHEMAS_FAILURE';

export const LIST = 'vtn/data-schema/LIST';
export const LIST_SUCCESS = 'vtn/data-schema/LIST_SUCCESS';
export const LIST_FAILURE = 'vtn/data-schema/LIST_FAILURE';

export const SET_SELECTED_SCHEMA_DEF =
  'vtn/data-schema/SET_SELECTED_SCHEMA_DEF';

export const SCHEMA_FILTER_CHANGE = 'vtn/data-schema/SCHEMA_FILTER_CHANGE';
export const SET_SCHEMA_FILTER = 'vtn/data-schema/SET_SCHEMA_FILTER';

const defaultState = {
  dataSchemasById: {},
  selectedSchemaDefs: {},
  isFetchingDataSchemas: false,
  failedFetchingDataSchemas: [],
  isFetchingRelatedSchemas: false,
  activeSchemaFilter: defaultFilter
};

const dataSchemasFilterMap = {
  'org-schemas': 'mine',
  'shared-schemas': 'others'
};

const reducer = reduceReducers(
  paginate({
    types: [LIST, LIST_SUCCESS, LIST_FAILURE],
    invalidateOnTypes: [CREATE_SUCCESS, DELETE_SUCCESS],
    idAttribute: 'id',
    keyBy: (state, meta) => get(meta, 'filter', defaultFilter),
    selectResponseResults: action =>
      get(action.payload, 'data.results.records'),
    selectResponseTotal: action =>
      get(action.payload, 'data.totalResults.count')
  }),
  createReducer(defaultState, {
    [FETCH](state, action) {
      const requestSuccessState = {
        ...state
      };

      return action.error
        ? this[FETCH_FAILURE](state, action)
        : requestSuccessState;
    },
    [FETCH_SUCCESS](state, action) {
      const dataSchema = action.payload.data.results;
      if (action.payload.errors && !dataSchema) {
        return this[FETCH_FAILURE](state, action);
      }

      if (!dataSchema) {
        return this[FETCH_FAILURE](state, action);
      }

      return {
        ...state,
        dataSchemasById: {
          ...state.dataSchemasById,
          [action.meta.dataRegistryId]: dataSchema
        },
        selectedSchemaDefs: {
          ...state.selectedSchemaDefs,
          [action.meta.dataRegistryId]: 0
        },
        failedFetchingDataSchemas: without(
          state.failedFetchingDataSchemas,
          action.meta.dataRegistryId
        )
      };
    },
    [FETCH_FAILURE](state, action) {
      return {
        ...state,
        failedFetchingDataSchemas: union(state.failedFetchingDataSchemas, [
          action.meta.dataRegistryId
        ])
      };
    },
    [UPDATE](state, action) {
      const requestSuccessState = {
        ...state
      };

      return action.error
        ? this[UPDATE_FAILURE](state, action)
        : requestSuccessState;
    },
    [DELETE](state, action) {
      const requestSuccessState = {
        ...state
      };

      return action.error
        ? this[DELETE_FAILURE](state, action)
        : requestSuccessState;
    },
    [UPDATE_SUCCESS](state, action) {
      const dataSchema = action.payload.data.results;

      if (action.payload.errors && !dataSchema) {
        return this[UPDATE_FAILURE](state, action);
      }

      return {
        ...state,
        dataSchemasById: {
          ...state.dataSchemasById,
          [action.meta.dataRegistryId]: {
            ...state.dataSchemasById[action.meta.dataRegistryId],
            ...dataSchema
          }
        }
      };
    },
    [UPDATE_SCHEMA_SUCCESS](state, action) {
      const updatedSchema = action.payload.data.results;

      if (action.payload.errors && !updatedSchema) {
        return this[UPDATE_FAILURE](state, action);
      }

      const currentSchemas =
        state.dataSchemasById[action.meta.dataRegistryId].schemas;
      let selectedSchema = state.selectedSchemaDefs[action.meta.dataRegistryId];

      const updatedSchemaIdx = currentSchemas.records.findIndex(
        schema => schema.id === updatedSchema.id
      );

      const schemas = omit(currentSchemas, 'records');
      schemas.records = [...currentSchemas.records];

      if (updatedSchemaIdx > -1) {
        // updated an existing schema record
        schemas.records[updatedSchemaIdx] = updatedSchema; // replace schema record with updated version
      } else {
        // a new 'draft' schema record was created
        schemas.records.push(updatedSchema);
        selectedSchema = schemas.records.length - 1;
      }

      return {
        ...state,
        dataSchemasById: {
          ...state.dataSchemasById,
          [action.meta.dataRegistryId]: {
            ...state.dataSchemasById[action.meta.dataRegistryId],
            schemas
          }
        },
        selectedSchemaDefs: {
          ...state.selectedSchemaDefs,
          [action.meta.dataRegistryId]: selectedSchema
        }
      };
    },
    [DELETE_SUCCESS](state, action) {
      const updatedSchema = action.payload.data.results;

      if (action.payload.errors && !updatedSchema) {
        return this[DELETE_FAILURE](state, action);
      }

      const currentSchemas =
        state.dataSchemasById[action.meta.dataRegistryId].schemas;

      const updatedSchemaIdx = currentSchemas.records.findIndex(
        schema => schema.id === updatedSchema.id
      );

      const schemas = omit(currentSchemas, 'records');
      schemas.records = [...currentSchemas.records];

      if (updatedSchemaIdx > -1) {
        // updated an existing schema record
        schemas.records.splice(updatedSchemaIdx, 1); // remove from list
        --schemas.count; // decrement count
      } else {
        return this[DELETE_FAILURE](state, action);
      }

      return {
        ...state,
        dataSchemasById: {
          ...state.dataSchemasById,
          [action.meta.dataRegistryId]: {
            ...state.dataSchemasById[action.meta.dataRegistryId],
            schemas
          }
        }
      };
    },
    [UPDATE_FAILURE](state, action) {
      return {
        ...state
      };
    },
    [DELETE_FAILURE](state, action) {
      return {
        ...state
      };
    },
    [LISTING_DATA_SCHEMAS](state, action) {
      return {
        ...state,
        isFetchingDataSchemas: true
      };
    },
    [DONE_LISTING_DATA_SCHEMAS](state, action) {
      return {
        ...state,
        isFetchingDataSchemas: false
      };
    },
    [LIST](state, action) {
      const requestSuccessState = {
        ...state
      };

      return action.error
        ? this[LIST_FAILURE](state, action)
        : requestSuccessState;
    },
    [LIST_SUCCESS](state, action) {
      if (action.payload.errors && !action.payload.data.results) {
        return this[LIST_FAILURE](state, action);
      }

      const registrySchemaDataRecords = action.payload.data.results.records;
      const selectedSchemaDefs = {};

      forEach(registrySchemaDataRecords, dataSchema => {
        selectedSchemaDefs[dataSchema.id] = 0;
      });

      return {
        ...state,
        dataSchemasById: {
          ...state.dataSchemasById,
          ...keyBy(registrySchemaDataRecords, 'id')
        },
        selectedSchemaDefs: {
          ...state.selectedSchemaDefs,
          ...selectedSchemaDefs
        },
        activeSchemaFilter: action.meta.filter
      };
    },
    [LIST_FAILURE](state, action) {
      return {
        ...state
      };
    },
    [LIST_RELATED_DATA_SCHEMAS](state, action) {
      if (action.error) {
        return this[LIST_RELATED_DATA_SCHEMAS_FAILURE](state, action);
      }

      return {
        ...state,
        isFetchingRelatedSchemas: true
      };
    },
    [LIST_RELATED_DATA_SCHEMAS_SUCCESS](state, action) {
      if (action.payload.errors && !action.payload.data.results) {
        return this[LIST_RELATED_DATA_SCHEMAS_FAILURE](state, action);
      }

      const payload = action.payload.data.results.records[0];
      if (!payload) {
        return this[LIST_RELATED_DATA_SCHEMAS_FAILURE](state, action);
      }

      const firstSchema =
        state.dataSchemasById[action.meta.dataRegistryId].schemas.records[0];
      const records = [
        firstSchema,
        // exclude the first schema from being added
        ...payload.schemas.records.filter(
          schema => schema.id !== firstSchema.id
        )
      ];
      return {
        ...state,
        dataSchemasById: {
          ...state.dataSchemasById,
          [action.meta.dataRegistryId]: {
            ...state.dataSchemasById[action.meta.dataRegistryId],
            schemas: {
              count: payload.schemas.count,
              records
            }
          }
        },
        isFetchingRelatedSchemas: false
      };
    },
    [LIST_RELATED_DATA_SCHEMAS_FAILURE](state, action) {
      return {
        ...state,
        isFetchingRelatedSchemas: false
      };
    },
    [SET_SELECTED_SCHEMA_DEF](state, action) {
      return {
        ...state,
        selectedSchemaDefs: {
          ...state.selectedSchemaDefs,
          [action.payload.dataRegistryId]: action.payload.schemaIdx
        }
      };
    },
    [SET_SCHEMA_FILTER](state, action) {
      return {
        ...state,
        activeSchemaFilter: action.payload.filter
      };
    }
  })
);

export default reducer;

function local(state) {
  return state[namespace];
}

export function getDataSchemas(state) {
  return local(state).dataSchemasById;
}

export function getDataSchema(state, dataRegistryId) {
  return local(state).dataSchemasById[dataRegistryId];
}

export function isFetchingDataSchemas(state) {
  return local(state).isFetchingDataSchemas;
}

export function isFetchingRelatedSchemas(state) {
  return local(state).isFetchingRelatedSchemas;
}

export function getFailedFetchingDataSchemas(state) {
  // failure for individually-fetched (by id) schemas
  return local(state).failedFetchingDataSchemas;
}

export const getPagedDataSchemas = createSelector(
  (state, filter) =>
    makeSelectors(
      filter || local(state).activeSchemaFilter
    ).selectPaginationOrder(local(state)),
  (state, filter) => local(state).dataSchemasById,
  (dataSchemaOrder = {}, dataSchemasById = {}) => {
    return mapValues(
      dataSchemaOrder,
      dataRegistryId => dataSchemasById[dataRegistryId]
    );
  }
);

export function getLoadingDataSchemas(state, filter) {
  return makeSelectors(
    filter || local(state).activeSchemaFilter
  ).selectFetchingEntities(local(state));
}

export function getFailedLoadingDataSchemas(state, filter) {
  return makeSelectors(
    filter || local(state).activeSchemaFilter
  ).selectFailedFetchingEntities(local(state));
}

export function getTotalDataSchemas(state, filter) {
  return makeSelectors(
    filter || local(state).activeSchemaFilter
  ).selectTotalEntities(local(state));
}

export function createDataSchema(values) {
  const dataRegistryId = uuidV4();
  // TODO: Fix GraphQL so it doesn't require source
  // 'source' field is being deprecated
  const fixedValues = Object.assign({}, { source: 'field deprecated' }, values);

  const query = `
  ${dataRegistryFieldsFragment}
  ${schemaFieldsFragment}
  mutation CreateDataRegistry($config: CreateDataRegistry!, $schema: UpsertSchemaDraft!) {
    createDataRegistry(input: $config) {
      ...dataRegistryFields
    }
    upsertSchemaDraft(input: $schema) {
      ...schemaFields
    }
  }
  `;

  return {
    [CALL_API]: {
      types: [CREATE, CREATE_SUCCESS, CREATE_FAILURE],
      endpoint: state => getConfig(state).services.graphqlUri,
      method: 'POST',
      body: JSON.stringify({
        query,
        operationName: 'CreateDataRegistry',
        variables: {
          config: { id: dataRegistryId, ...omit(fixedValues, 'schema') },
          schema: { dataRegistryId, ...pick(fixedValues, 'schema') }
        }
      }),
      headers: commonHeaders
    }
  };
}

export function fetchDataSchema(dataRegistryId, { force } = {}) {
  const query = `
    ${dataRegistryFieldsFragment}
    ${orderedSchemaListFragment}
    query ($id: ID!) {
      results: dataRegistry(id: $id) {
        ...dataRegistryFields
        ...orderedSchemaList
      }
    }`;

  return {
    [CALL_API]: {
      types: [FETCH, FETCH_SUCCESS, FETCH_FAILURE].map(type => ({
        type,
        meta: { dataRegistryId }
      })),
      endpoint: state => getConfig(state).services.graphqlUri,
      method: 'POST',
      body: JSON.stringify({
        query,
        variables: { id: dataRegistryId }
      }),
      headers: commonHeaders,
      bailout: state => {
        if (force) return false;

        const registryData = getDataSchemas(state)[dataRegistryId];
        return registryData && registryData.schemas.records.length > 1;
      }
    }
  };
}

export function updateSchemaConfig(dataRegistryId, values) {
  // TODO: Fix GraphQL so it doesn't require source
  // 'source' field is being deprecated
  const fixedValues = Object.assign({}, { source: 'field deprecated' }, values);
  const query = `
  ${dataRegistryFieldsFragment}
  mutation UpdateDataRegistry($input: UpdateDataRegistry!) {
    results: updateDataRegistry(input: $input) {
      ...dataRegistryFields
    }
  }`;

  return {
    [CALL_API]: {
      types: [UPDATE, UPDATE_SUCCESS, UPDATE_FAILURE].map(a => ({
        type: a,
        meta: { dataRegistryId }
      })),
      endpoint: state => getConfig(state).services.graphqlUri,
      method: 'POST',
      body: JSON.stringify({
        query,
        variables: { input: { id: dataRegistryId, ...fixedValues } }
      }),
      headers: commonHeaders
    }
  };
}

export function updateSchemaDefinition(dataRegistryId, schema, majorVersion) {
  const query = `
  ${schemaFieldsFragment}
  mutation UpsertSchemaDraft($input: UpsertSchemaDraft!) {
    results: upsertSchemaDraft(input: $input) {
      ...schemaFields
    }
  }`;

  return {
    [CALL_API]: {
      types: [UPDATE, UPDATE_SCHEMA_SUCCESS, UPDATE_FAILURE].map(a => ({
        type: a,
        meta: { dataRegistryId }
      })),
      endpoint: state => getConfig(state).services.graphqlUri,
      method: 'POST',
      body: JSON.stringify({
        query,
        variables: { input: { dataRegistryId, schema, majorVersion } }
      }),
      headers: commonHeaders
    }
  };
}

export function updateSchemaStatus(
  { id, dataRegistryId },
  { status, breakingChanges = false }
) {
  const query = `
  ${schemaFieldsFragment}
  mutation UpdateSchemaState($input: UpdateSchemaState!) {
    results: updateSchemaState(input: $input) {
      ...schemaFields
    }
  }`;

  const types =
    status === 'deleted'
      ? [DELETE, DELETE_SUCCESS, DELETE_FAILURE]
      : [UPDATE, UPDATE_SCHEMA_SUCCESS, UPDATE_FAILURE];

  return {
    [CALL_API]: {
      // types: [UPDATE, UPDATE_SCHEMA_SUCCESS, UPDATE_FAILURE].map(a => ({
      types: types.map(a => ({
        type: a,
        meta: { dataRegistryId, schemaId: id }
      })),
      endpoint: state => getConfig(state).services.graphqlUri,
      method: 'POST',
      body: JSON.stringify({
        query,
        variables: { input: { id, status, breakingChanges } }
      }),
      headers: commonHeaders
    }
  };
}

export function listDataSchemas(
  { offset = 0, limit = 10, filter = defaultFilter } = {},
  { force } = {}
) {
  const requestId = guid();

  const query = `
    ${dataRegistryFieldsFragment}
    ${schemaFieldsFragment}
    query($limit: Int, $offset: Int, $filterByOwnership: SchemaOwnership) {
      results: dataRegistries(limit: $limit, offset: $offset, filterByOwnership: $filterByOwnership) {
        count
        records {
        	...dataRegistryFields
          schemas(
            status: [published, draft, inactive],
            orderBy: [
              { field: status, direction: asc },
              { field: majorVersion, direction: desc },
              { field: minorVersion, direction: desc }
            ]
          ) {
            count
            records {
              ...schemaFields
            }
          }
        }
      }
      totalResults: dataRegistries(limit: 9999, filterByOwnership: $filterByOwnership) {
        count
      }
    }
  `;

  return {
    [CALL_API]: {
      types: [LIST, LIST_SUCCESS, LIST_FAILURE].map(type => ({
        type,
        meta: { requestId, offset, limit, filter }
      })),
      endpoint: state =>
        `${getConfig(state).services.graphqlUri}?query=${query}`,
      method: 'POST',
      body: JSON.stringify({
        query,
        variables: {
          limit,
          offset,
          filterByOwnership: dataSchemasFilterMap[filter]
        }
      }),
      headers: commonHeaders,
      bailout: state => {
        if (force) return false;

        const dataSchemas = getPagedDataSchemas(state, filter);
        const needStart = offset;
        const needEnd = offset + limit - 1;

        // bail if all needed data schemas exist
        return every(range(needStart, needEnd + 1), index => {
          return !!dataSchemas[index];
        });
      }
    }
  };
}

export function listingDataSchemas() {
  return {
    type: LISTING_DATA_SCHEMAS
  };
}

export function doneListingDataSchemas() {
  return {
    type: DONE_LISTING_DATA_SCHEMAS
  };
}

export function getSelectedSchemas(state) {
  return local(state).selectedSchemaDefs;
}

export function getActiveSchemaFilter(state) {
  return local(state).activeSchemaFilter;
}

export function listRelatedDataSchemas(dataRegistryId) {
  const query = `
    ${orderedSchemaListFragment}
    query ($id: ID!) {
      results: dataRegistries(id: $id) {
        records {
          id
          ...orderedSchemaList
        }
      }
    }`;

  return {
    [CALL_API]: {
      types: [
        LIST_RELATED_DATA_SCHEMAS,
        LIST_RELATED_DATA_SCHEMAS_SUCCESS,
        DONE_LISTING_DATA_SCHEMAS
      ].map(type => ({
        type,
        meta: { dataRegistryId }
      })),
      endpoint: state =>
        `${getConfig(state).services.graphqlUri}?query=${query}`,
      method: 'POST',
      body: JSON.stringify({
        query,
        variables: { id: dataRegistryId }
      }),
      headers: commonHeaders,
      bailout: state =>
        getDataSchema(state, dataRegistryId).schemas.records.length > 1
    }
  };
}

export function selectSchema(dataRegistryId, schemaIdx) {
  return {
    type: SET_SELECTED_SCHEMA_DEF,
    payload: {
      dataRegistryId,
      schemaIdx
    }
  };
}

export function takeDataSchemaAction(action, registrySchemaData) {
  const states = {
    publish: {
      status: 'published',
      confirmationMsg: `Are you sure you want to ${action} this schema?`
    },
    delete: {
      status: 'deleted',
      confirmationMsg: `Are you sure you want to ${action} this schema? You won't be able to undo this action`
    },
    deactivate: {
      status: 'inactive',
      confirmationMsg: `Are you sure you want to ${action} this schema?`
    }
  };

  if (states[action]) {
    return confirmSchemaAction(registrySchemaData, states[action], action);
  }
}

export const confirmSchemaAction = withConfirmation(
  updateSchemaStatus,
  (data, { confirmationMsg }, action) => ({
    message: confirmationMsg,
    confirmButtonLabel: `${action}`,
    cancelButtonLabel: 'Cancel'
  })
);

/* FRAGMENTS */
const dataRegistryFieldsFragment = `
  fragment dataRegistryFields on DataRegistry {
    id
    name
    source
    description
    organizationId
    createdDateTime
    createdBy {
      id
    }
    modifiedBy {
      id
    }
    modifiedDateTime
  }
`;
const schemaFieldsFragment = `
  fragment schemaFields on Schema {
    id
    dataRegistryId
    definition
    majorVersion
    minorVersion
    status
    validActions
    createdBy {
      id
    }
    createdDateTime
    modifiedBy {
      id
    }
    modifiedDateTime
  }`;
const orderedSchemaListFragment = `
  ${schemaFieldsFragment}
  fragment orderedSchemaList on DataRegistry {
    schemas(
      status: [published, draft, inactive],
      orderBy: [
        { field: status, direction: asc },
        { field: majorVersion, direction: desc },
        { field: minorVersion, direction: desc }
      ]
    ) {
      count
      records {
        ...schemaFields
      }
    }
  }`;



// WEBPACK FOOTER //
// ./src/client/modules/data-schema/index.js